import React, { useState, useCallback, useEffect, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SolidCheckCircleIcon from 'components/Luxkit/Icons/solid/SolidCheckCircleIcon'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import InlineGroup from 'components/Common/InlineGroup'
import debounce from 'lodash.debounce'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'

const Container = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(32)} 0;
`

const StepItem = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const IconAnimation = keyframes`
  0% {
    transform: scale(0.5);
  }
  70% {
    transform: scale(1.2);
  }
  80% {
    transform: scale(1);
  }
  90% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`

const LoadingCheckedIcon = styled(SolidCheckCircleIcon)`
  animation: ${IconAnimation} .6s ease-in-out;
  animation-fill-mode: forwards;
`

type Props = {
  loading: boolean
  cruiseLine: App.CruiseLine
}

function CruiseSelectionModalLoading({ cruiseLine, loading }: Props) {
  const stepDuration = 2000
  const [loadingStep, setLoadingStep] = useState(0)

  const startLastStep = useMemo(() => debounce(() => {
    setLoadingStep(3)
  }, stepDuration), [setLoadingStep])

  const startSecondStep = useMemo(() => debounce(() => {
    setLoadingStep(2)
    startLastStep()
  }, stepDuration), [setLoadingStep, startLastStep])

  const startFirstStep = useCallback(() => {
    setLoadingStep(1)
    startSecondStep()
  }, [setLoadingStep, startSecondStep])

  useEffect(() => {
    if (loading) {
      startFirstStep()
    }
  }, [loading, startFirstStep])

  const steps = useMemo(() => {
    return [
      'Searching for available cabins',
      'Getting the best prices',
      'Retrieving details of cabins',
    ].map((step, index) => ({
      done: loadingStep > (index + 1),
      title: step,
    }))
  }, [loadingStep])

  return (loading &&
    <Container gap={48}>
      <BodyText
        variant="large"
        weight="bold"
        align="center"
        colour="neutral-two"
      >Connecting to {cruiseLine?.name}</BodyText>

      <Image id={cruiseLine?.imageId} />

      <InlineGroup spacing="xxl">
        {steps.map((step) => (
          <StepItem
            gap={12}
            key={step.title}
          >
            <BodyText
              variant="medium"
              weight="semi-bold"
              align="center"
              colour="neutral-two"
            >{step.title}</BodyText>

            {step.done && <LoadingCheckedIcon size={30} colour="highlight-primary-normal" />}
            {!step.done && <LoadingSpinner />}
          </StepItem>
        ))}
      </InlineGroup>
    </Container>
  )
}

export default CruiseSelectionModalLoading
